"use client";
import { notifications } from "@mantine/notifications";
import { ProductGroupActions } from "Components/Admin/AdminProduct/AdminProductGroup/ProductGroupActions";
import { ProductGroupForm } from "Components/Admin/AdminProduct/AdminProductGroup/ProductGroupForm";
import { ProductGroupTable } from "Components/Admin/AdminProduct/AdminProductGroup/ProductGroupTable";
import { Header } from "Components/Admin/StyleComponent/Header";
import { useMemo, useState } from "react";
import {
  useCreateItemGroupMutation,
  useDeleteItemGroupMutation,
  useDeleteItemGroupsBulkMutation,
  useGetAllItemGroupsQuery,
  useUpdateItemGroupMutation,
} from "Services/Admin/ProductGroupAdminApiSlice";
import { ItemGroup } from "Types/Admin/AdminProductGroupType";

const PLANT = process.env.REACT_APP_PLANT;

const AdminProductGroup = () => {
  const {
    data,
    isLoading,
    refetch,
  } = useGetAllItemGroupsQuery(PLANT);

  const itemGroups = useMemo(() => {
    return Array.isArray(data) ? data : [];
  }, [data]);

  const [createItemGroup, { isLoading: isCreating }] =
  useCreateItemGroupMutation();

const [updateItemGroup, { isLoading: isUpdating }] =
  useUpdateItemGroupMutation();

const formLoading = isCreating || isUpdating;
  const [deleteItemGroup] = useDeleteItemGroupMutation();
  const [deleteItemGroupsBulk] = useDeleteItemGroupsBulkMutation();

  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editingItemGroup, setEditingItemGroup] = useState<ItemGroup | null>(
    null,
  );
  const [selectedItemGroupId, setSelectedItemGroupId] = useState<number | null>(
    null,
  );
  const [selectedItemGroupIds, setSelectedItemGroupIds] = useState<number[]>(
    [],
  );

  const [pageSize, setPageSize] = useState(100);
  const [activePage, setActivePage] = useState(1);

  const [searchText, setSearchText] = useState("");

  const filteredItemGroups = useMemo(() => {
    if (!itemGroups) return [];
    return searchText
      ? itemGroups.filter((itemGroup) =>
          (itemGroup.itemGroupName || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()),
        )
      : [...itemGroups];
  }, [itemGroups, searchText]);

  const paginatedItemGroups = useMemo(() => {
    return filteredItemGroups.slice(
      (activePage - 1) * pageSize,
      activePage * pageSize,
    );
  }, [filteredItemGroups, activePage, pageSize]);

  const handleBulkDeleteItemGroups = async (ids: number[]) => {
    if (!ids || ids.length === 0) return;

    try {
      const response = await deleteItemGroupsBulk({
        ids,
        plant: PLANT,
      }).unwrap();

      notifications.show({
        message: response.message,
        color: "teal",
      });

      refetch();
      setSelectedItemGroupIds([]);
      setSelectedItemGroupId(null);
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
      console.error("Failed to bulk delete item groups:", error);
    }
  };

  const handleViewItemGroup = async (id: number) => {
    try {
      const itemGroupToView = itemGroups.find(
        (itemGroup) => itemGroup.id === id,
      );
      if (itemGroupToView) {
        setEditingItemGroup(itemGroupToView);
        setViewMode(true);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Failed to get item group details:", error);
    }
  };

  const handleEditItemGroup = async (id: number) => {
    try {
      const itemGroupToEdit = itemGroups.find(
        (itemGroup) => itemGroup.id === id,
      );
      if (itemGroupToEdit) {
        setEditingItemGroup(itemGroupToEdit);
        setViewMode(false);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Failed to get item group details:", error);
    }
  };

  const handleSubmit = async (data: { itemGroupName: string }) => {
    try {
      let response;
      if (editingItemGroup) {
        response = await updateItemGroup({
          id: editingItemGroup.id,
          itemGroupName: data.itemGroupName,
          plant: PLANT,
        }).unwrap();
      } else {
        response = await createItemGroup({
          itemGroupName: data.itemGroupName,
          plant: PLANT,
        }).unwrap();
      }

      notifications.show({
        message: response.message,
        color: "teal",
      });

      setShowForm(false);
      setEditingItemGroup(null);
      refetch();
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
      console.error("Failed to save item group:", error);
    }
  };

  const handleDeleteItemGroup = async (id: number) => {
    try {
      const response = await deleteItemGroup({ id, plant: PLANT }).unwrap();

      notifications.show({
        message: response.message,
        color: "teal",
      });
      refetch();
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
      console.error("Failed to delete item group:", error);
    }
  };
  return (
    <div className="font-body p-6 mx-auto">
      {!showForm ? (
        <>
          <Header
            title="Product Groups"
            onAdd={() => setShowForm(true)}
            addLabel="Add Product Group"
          />
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden p-4 pt-8">
            <ProductGroupActions
              onSearchChange={setSearchText}
              isDeleteDisabled={selectedItemGroupIds.length === 0}
              onDelete={() => handleBulkDeleteItemGroups(selectedItemGroupIds)}
              selectedCount={selectedItemGroupIds.length}
            />
            <ProductGroupTable
              itemGroups={paginatedItemGroups}
              onSelectItemGroup={setSelectedItemGroupId}
              onDeleteItemGroup={handleDeleteItemGroup}
              onViewItemGroup={handleViewItemGroup}
              onEditItemGroup={handleEditItemGroup}
              selectedItemGroup={selectedItemGroupId}
              isLoading={isLoading}
              selectedGroups={selectedItemGroupIds}
              onSelectedGroupsChange={setSelectedItemGroupIds}
              itemsPerPage={pageSize}
              totalItems={filteredItemGroups.length}
              onItemsPerPageChange={setPageSize}
              onPageChange={setActivePage}
              currentPage={activePage}
            />
          </div>
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingItemGroup(null);
              setViewMode(false);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left-icon lucide-arrow-left h-4 w-4"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            <span className="text-sm">Back</span>
          </button>
          <Header
            title={
              viewMode
                ? "View Product Group"
                : editingItemGroup
                  ? "Edit Product Group"
                  : "Add Product Group"
            }
          />
          <ProductGroupForm
            initialValues={
              editingItemGroup
                ? { itemGroupName: editingItemGroup.itemGroupName }
                : { itemGroupName: "" }
            }
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setViewMode(false);
              setEditingItemGroup(null);
            }}
            mode={viewMode ? "view" : editingItemGroup ? "edit" : "add"}
            loading={formLoading}
          />
        </>
      )}
    </div>
  );
};

export default AdminProductGroup;
